import { openWindowSafe } from '@/lib/windowUtils'
import type { PlatformUser } from '@/stores/auth'
import { useUser } from '@/stores/authStore'
import { getConsoleUrl, isConsoleDeployment, isDemoDeployment } from '@/utils/deployment'
import { useCallback, useEffect, useState } from 'react'

interface UseCreateOrgNavigationReturn {
	isConsole: boolean
	showCreateDialog: boolean
	setShowCreateDialog: (show: boolean) => void
	handleOpenConsoleOrgCreateModal: () => void
}

/**
 * Hook to handle organization creation navigation across deployments.
 * On console deployments, opens the create dialog directly.
 * On platform deployments, redirects to the console org billing page with auto-open param.
 */
export function useCreateOrgNavigation(currentOrgId?: string | null): UseCreateOrgNavigationReturn {
	const user = useUser() as PlatformUser | null
	const isConsole = isConsoleDeployment() || isDemoDeployment()
	const [showCreateDialog, setShowCreateDialog] = useState(false)

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const params = new URLSearchParams(window.location.search)
			if (params.get('createOrg') === 'true' || params.get('create') === 'true') {
				setShowCreateDialog(true)
				const url = new URL(window.location.href)
				url.searchParams.delete('createOrg')
				url.searchParams.delete('create')
				const newSearch = url.searchParams.toString()
				const newUrl = url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash
				window.history.replaceState({}, '', newUrl)
			}
		}
	}, [])

	const handleOpenConsoleOrgCreateModal = useCallback(() => {
		if (isConsole) {
			setShowCreateDialog(true)
		} else {
			const redirectUrl = getConsoleUrl()
			const targetOrgId = currentOrgId || user?.organizations?.[0]?.id
			if (targetOrgId) {
				openWindowSafe(`${redirectUrl}/orgs/${targetOrgId}/billing/plan-details?createOrg=true`)
			} else {
				openWindowSafe(`${redirectUrl}/welcome?createOrg=true`)
			}
		}
	}, [isConsole, currentOrgId, user?.organizations])

	return {
		isConsole,
		showCreateDialog,
		setShowCreateDialog,
		handleOpenConsoleOrgCreateModal,
	}
}
